import { createFileRoute } from '@tanstack/react-router'
import { Bell, Check, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import type { ReactNode } from 'react'

import type {
    CarePeriodicRule,
    CareReminderRule,
    TimeUnit,
} from '@/lib/api/care-cycle-api'
import { PageBackButton } from '@/components/common/PageBackButton'
import { FormSection } from '@/components/forms/form-section'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { Card } from '@/components/ui/card'
import {
    useAddCareCycleRule,
    useAddPeriodicRule,
    useCareCycleRules,
    useCarePeriodicRules,
    useDeleteCareCycleRule,
    useDeletePeriodicRule,
    useUpdateCareCycleRule,
    useUpdatePeriodicRule,
} from '@/hooks/use-care-cycle-query'
import { m } from '@/lib/paraglide/messages'
import { cn } from '@/lib/utils'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/agriculture/care-reminders',
)({
    component: CareRemindersPage,
})

// Activity slugs in a sensible display order, with their Vietnamese labels.
const ACTIVITIES: Array<{ value: string; label: () => string }> = [
    { value: 'fertilizing', label: m.care_log_activity_fertilizing },
    { value: 'spraying', label: m.care_log_activity_spraying },
    { value: 'harvesting', label: m.care_log_activity_harvesting },
    { value: 'bud_picking', label: m.care_log_activity_bud_picking },
    { value: 'weeding', label: m.care_log_activity_weeding },
    { value: 'watering', label: m.care_log_activity_watering },
    { value: 'pruning', label: m.care_log_activity_pruning },
    { value: 'replanting', label: m.care_log_activity_replanting },
    { value: 'other', label: m.care_log_activity_other },
]

function labelOf(slug: string): string {
    return ACTIVITIES.find((a) => a.value === slug)?.label() ?? slug
}

const UNITS: Array<{ value: TimeUnit; label: () => string }> = [
    { value: 'day', label: m.care_cycle_unit_day },
    { value: 'hour', label: m.care_cycle_unit_hour },
    { value: 'minute', label: m.care_cycle_unit_minute },
]

function sameSet(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false
    const sb = new Set(b)
    return a.every((x) => sb.has(x))
}

/** Multi-select of activities as a popover checklist; selected shown as chips. */
function ActivityMultiSelect({
    value,
    onChange,
    invalid,
}: {
    value: string[]
    onChange: (next: string[]) => void
    invalid?: boolean
}) {
    const toggle = (slug: string) =>
        onChange(
            value.includes(slug)
                ? value.filter((v) => v !== slug)
                : [...value, slug],
        )
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    aria-invalid={invalid}
                    className={cn(
                        'flex min-h-9 w-full min-w-52 flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1.5 text-left text-sm',
                        invalid && 'border-destructive',
                    )}
                >
                    {value.length === 0 ? (
                        <span className="truncate whitespace-nowrap px-1 text-muted-foreground">
                            {m.care_cycle_to_placeholder()}
                        </span>
                    ) : (
                        value.map((slug) => (
                            <span
                                key={slug}
                                className="inline-flex items-center gap-1 rounded bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
                            >
                                {labelOf(slug)}
                                <span
                                    role="button"
                                    tabIndex={-1}
                                    aria-label={m.care_cycle_remove_activity()}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        toggle(slug)
                                    }}
                                    className="cursor-pointer opacity-70 hover:opacity-100"
                                >
                                    ✕
                                </span>
                            </span>
                        ))
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-1" align="start">
                {ACTIVITIES.map((a) => {
                    const on = value.includes(a.value)
                    return (
                        <button
                            key={a.value}
                            type="button"
                            onClick={() => toggle(a.value)}
                            className={cn(
                                'flex w-full items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-accent',
                                on && 'font-medium text-primary',
                            )}
                        >
                            {a.label()}
                            {on && <Check className="size-4" />}
                        </button>
                    )
                })}
            </PopoverContent>
        </Popover>
    )
}

function ActivitySelect({
    value,
    onChange,
    disabled,
    invalid,
}: {
    value: string
    onChange?: (v: string) => void
    disabled?: boolean
    invalid?: boolean
}) {
    return (
        // Controlled with the raw value ('' = nothing selected). Do NOT coerce
        // to undefined — that flips Radix Select to uncontrolled and it keeps
        // showing the last pick after a reset, desyncing from React state.
        <Select value={value} onValueChange={onChange} disabled={disabled}>
            <SelectTrigger
                aria-invalid={invalid}
                className={cn('min-w-40', invalid && 'border-destructive')}
            >
                <SelectValue placeholder={m.care_cycle_from_placeholder()} />
            </SelectTrigger>
            <SelectContent>
                {ACTIVITIES.map((a) => (
                    <SelectItem key={a.value} value={a.value}>
                        {a.label()}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    )
}

function AmountUnitInput({
    amount,
    unit,
    onAmount,
    onUnit,
    invalid,
}: {
    amount: number
    unit: TimeUnit
    onAmount: (n: number) => void
    onUnit: (u: TimeUnit) => void
    invalid?: boolean
}) {
    return (
        <span className="inline-flex items-center gap-2">
            <Input
                type="number"
                min={1}
                inputMode="numeric"
                aria-invalid={invalid}
                className={cn('w-16', invalid && 'border-destructive')}
                value={Number.isFinite(amount) ? amount : ''}
                onChange={(e) => onAmount(Math.floor(Number(e.target.value)))}
            />
            <Select value={unit} onValueChange={(v) => onUnit(v as TimeUnit)}>
                <SelectTrigger className="w-[5.5rem]">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {UNITS.map((u) => (
                        <SelectItem key={u.value} value={u.value}>
                            {u.label()}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </span>
    )
}

function DeleteButton({
    onClick,
    disabled,
}: {
    onClick: () => void
    disabled?: boolean
}) {
    return (
        <Button
            size="icon"
            variant="ghost"
            className="text-muted-foreground hover:text-destructive"
            aria-label={m.action_delete()}
            onClick={onClick}
            disabled={disabled}
        >
            <Trash2 className="size-4" />
        </Button>
    )
}

// ---------------- Card shell (shared) ----------------

// CardShell renders a rule as a sentence-style card: the editable sentence on
// the left, the enable/delete controls stacked top-right, and an optional
// footer (the Save button that appears when the draft is dirty).
function CardShell({
    disabled,
    controls,
    footer,
    children,
}: {
    disabled?: boolean
    controls: ReactNode
    footer?: ReactNode
    children: ReactNode
}) {
    return (
        <Card className={cn('gap-0 p-4', disabled && 'opacity-60')}>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1 space-y-2.5">{children}</div>
                <div className="flex shrink-0 flex-col items-end gap-2">
                    {controls}
                </div>
            </div>
            {footer}
        </Card>
    )
}

// SentenceLine is one clause of the rule sentence: a muted connector label
// followed by its control. `alignTop` lifts the label for the multi-select
// clause whose chips can wrap onto several lines. `error` shows a specific
// Vietnamese validation message under the control (never a disabled input).
function SentenceLine({
    label,
    alignTop,
    error,
    children,
}: {
    label: ReactNode
    alignTop?: boolean
    error?: string
    children: ReactNode
}) {
    return (
        <div>
            <div
                className={cn(
                    'flex flex-wrap gap-x-2 gap-y-1.5',
                    alignTop ? 'items-start' : 'items-center',
                )}
            >
                <span
                    className={cn(
                        'text-sm text-muted-foreground',
                        alignTop && 'pt-2',
                    )}
                >
                    {label}
                </span>
                {children}
            </div>
            {error && (
                <p role="alert" className="mt-1 text-xs text-destructive">
                    {error}
                </p>
            )}
        </div>
    )
}

// MessageBodyField is the per-rule push text: what the household actually reads
// in the notification. Full-width under the sentence since it holds a sentence
// of its own, not a single token.
function MessageBodyField({
    value,
    onChange,
    error,
}: {
    value: string
    onChange: (v: string) => void
    error?: string
}) {
    return (
        <div className="pt-1">
            <p className="mb-1.5 text-sm text-muted-foreground">
                {m.care_cycle_message_label()}
            </p>
            <Textarea
                rows={2}
                value={value}
                aria-invalid={!!error}
                maxLength={MAX_MESSAGE_LEN}
                placeholder={m.care_cycle_message_placeholder()}
                className={cn(
                    'resize-y',
                    error &&
                        'border-destructive focus-visible:ring-destructive/40',
                )}
                onChange={(e) => onChange(e.target.value)}
            />
            {error ? (
                <p role="alert" className="mt-1 text-xs text-destructive">
                    {error}
                </p>
            ) : (
                <p className="mt-1 text-xs text-muted-foreground">
                    {m.care_cycle_message_hint()}
                </p>
            )}
        </div>
    )
}

// ---------------- Validation ----------------

// MAX_MESSAGE_LEN mirrors the server-side cap (maxMessageBodyLen) so the field
// stops accepting input at the same point the API would reject it.
const MAX_MESSAGE_LEN = 500

interface WorkflowErrors {
    from?: string
    to?: string
    amount?: string
    message?: string
}

// messageError validates the push body: required, and within the server cap.
function messageError(raw: string): string | undefined {
    const body = raw.trim()
    if (!body) return m.care_cycle_err_message()
    if (body.length > MAX_MESSAGE_LEN) return m.care_cycle_err_message_long()
    return undefined
}

function validateWorkflow(d: {
    from_activity: string
    to_activities: string[]
    amount: number
    message_body: string
}): WorkflowErrors {
    const errs: WorkflowErrors = {}
    if (!d.from_activity) errs.from = m.care_cycle_err_from()
    if (d.to_activities.length === 0) errs.to = m.care_cycle_err_to()
    if (!Number.isFinite(d.amount) || d.amount <= 0)
        errs.amount = m.care_cycle_err_amount()
    errs.message = messageError(d.message_body)
    return errs
}

function amountError(amount: number): string | undefined {
    return !Number.isFinite(amount) || amount <= 0
        ? m.care_cycle_err_amount()
        : undefined
}

function hasErrors(errs: object): boolean {
    return Object.values(errs).some(Boolean)
}

// ---------------- Workflow cards ----------------

interface WorkflowDraft {
    to_activities: string[]
    amount: number
    unit: TimeUnit
    message_body: string
}

function WorkflowRow({ rule }: { rule: CareReminderRule }) {
    const update = useUpdateCareCycleRule()
    const del = useDeleteCareCycleRule()
    // from_activity KHÔNG nằm trong draft: nó là định danh của rule (server
    // không update cột này) — cho sửa rồi vứt lặng lẽ còn tệ hơn khoá hẳn.
    const [draft, setDraft] = useState<WorkflowDraft>({
        to_activities: rule.to_activities,
        amount: rule.amount,
        unit: rule.unit,
        message_body: rule.message_body,
    })

    const [errors, setErrors] = useState<WorkflowErrors>({})

    const dirty =
        draft.amount !== rule.amount ||
        draft.unit !== rule.unit ||
        draft.message_body !== rule.message_body ||
        !sameSet(draft.to_activities, rule.to_activities)

    // save runs the mutation; the mutation hook's onError shows the error toast,
    // so on failure we only skip the success toast (no second toast here).
    // Returns whether the write landed so callers can sync local state.
    async function save(
        body: WorkflowDraft & { from_activity: string; enabled: boolean },
    ): Promise<boolean> {
        try {
            await update.mutateAsync({ id: rule.id, body })
            toast.success(m.care_cycle_rule_saved())
            return true
        } catch {
            // Error toast handled by the mutation's onError.
            return false
        }
    }

    // Save button: validate the draft, then persist it (keeping current enabled).
    async function persistDraft() {
        const errs = validateWorkflow({
            from_activity: rule.from_activity,
            to_activities: draft.to_activities,
            amount: draft.amount,
            message_body: draft.message_body,
        })
        setErrors(errs)
        if (hasErrors(errs)) return
        const cleaned = { ...draft, message_body: draft.message_body.trim() }
        if (
            await save({
                ...cleaned,
                from_activity: rule.from_activity,
                enabled: rule.enabled,
            })
        ) {
            // Đồng bộ draft với giá trị đã lưu (body đã trim) — không thì so
            // sánh dirty lệch mãi và nút Lưu không chịu ẩn.
            setDraft(cleaned)
        }
    }

    // Toggle: persist only the enabled flag from the SAVED rule — never the
    // unsaved draft edits, and never blocked by draft validation.
    function toggleEnabled(enabled: boolean) {
        void save({
            from_activity: rule.from_activity,
            to_activities: rule.to_activities,
            amount: rule.amount,
            unit: rule.unit,
            message_body: rule.message_body,
            enabled,
        })
    }

    return (
        <CardShell
            disabled={!rule.enabled}
            controls={
                <>
                    <Switch
                        checked={rule.enabled}
                        disabled={update.isPending}
                        onCheckedChange={toggleEnabled}
                        aria-label={m.care_cycle_col_enabled()}
                    />
                    <DeleteButton
                        disabled={del.isPending}
                        onClick={async () => {
                            // Xoá rule cascade huỷ luôn reminder đã arm của các
                            // hộ — phải hỏi lại, không có undo.
                            if (!window.confirm(m.care_cycle_delete_confirm()))
                                return
                            await del.mutateAsync(rule.id)
                            toast.success(m.care_cycle_rule_deleted())
                        }}
                    />
                </>
            }
            footer={
                dirty && (
                    <div className="mt-3 flex justify-end border-t pt-3">
                        <Button
                            size="sm"
                            onClick={() => void persistDraft()}
                            disabled={update.isPending}
                        >
                            {m.care_cycle_save()}
                        </Button>
                    </div>
                )
            }
        >
            <SentenceLine label={m.care_cycle_sentence_after()}>
                {/* from_activity là định danh của rule — muốn nguồn khác thì
                    tạo rule mới, giống ô công đoạn của nhắc định kỳ. */}
                <ActivitySelect value={rule.from_activity} disabled />
            </SentenceLine>
            <SentenceLine
                label={m.care_cycle_sentence_then()}
                alignTop
                error={errors.to}
            >
                <ActivityMultiSelect
                    value={draft.to_activities}
                    invalid={!!errors.to}
                    onChange={(next) => {
                        setDraft((d) => ({ ...d, to_activities: next }))
                        setErrors((e) => ({ ...e, to: undefined }))
                    }}
                />
            </SentenceLine>
            <SentenceLine
                label={m.care_cycle_sentence_delay()}
                error={errors.amount}
            >
                <AmountUnitInput
                    amount={draft.amount}
                    unit={draft.unit}
                    invalid={!!errors.amount}
                    onAmount={(n) => {
                        setDraft((d) => ({ ...d, amount: n }))
                        setErrors((e) => ({ ...e, amount: undefined }))
                    }}
                    onUnit={(u) => setDraft((d) => ({ ...d, unit: u }))}
                />
            </SentenceLine>
            <MessageBodyField
                value={draft.message_body}
                error={errors.message}
                onChange={(v) => {
                    setDraft((d) => ({ ...d, message_body: v }))
                    setErrors((e) => ({ ...e, message: undefined }))
                }}
            />
        </CardShell>
    )
}

function AddWorkflowRow() {
    const add = useAddCareCycleRule()
    const [from, setFrom] = useState('')
    const [to, setTo] = useState<string[]>([])
    const [amount, setAmount] = useState(7)
    const [unit, setUnit] = useState<TimeUnit>('day')
    const [messageBody, setMessageBody] = useState('')
    const [errors, setErrors] = useState<WorkflowErrors>({})

    async function submit() {
        const errs = validateWorkflow({
            from_activity: from,
            to_activities: to,
            amount,
            message_body: messageBody,
        })
        setErrors(errs)
        if (hasErrors(errs)) return
        try {
            await add.mutateAsync({
                body: {
                    from_activity: from,
                    to_activities: to,
                    amount,
                    unit,
                    enabled: true,
                    message_body: messageBody.trim(),
                },
            })
            toast.success(m.care_cycle_rule_saved())
            setFrom('')
            setTo([])
            setAmount(7)
            setUnit('day')
            setMessageBody('')
            setErrors({})
        } catch {
            // Error toast handled by the mutation's onError.
        }
    }

    return (
        <Card className="gap-0 border-dashed p-4">
            <p className="mb-3 text-sm font-medium text-muted-foreground">
                {m.care_cycle_new_workflow_title()}
            </p>
            <div className="space-y-2.5">
                <SentenceLine
                    label={m.care_cycle_sentence_after()}
                    error={errors.from}
                >
                    <ActivitySelect
                        value={from}
                        invalid={!!errors.from}
                        onChange={(v) => {
                            setFrom(v)
                            setErrors((e) => ({ ...e, from: undefined }))
                        }}
                    />
                </SentenceLine>
                <SentenceLine
                    label={m.care_cycle_sentence_then()}
                    alignTop
                    error={errors.to}
                >
                    <ActivityMultiSelect
                        value={to}
                        invalid={!!errors.to}
                        onChange={(next) => {
                            setTo(next)
                            setErrors((e) => ({ ...e, to: undefined }))
                        }}
                    />
                </SentenceLine>
                <SentenceLine
                    label={m.care_cycle_sentence_delay()}
                    error={errors.amount}
                >
                    <AmountUnitInput
                        amount={amount}
                        unit={unit}
                        invalid={!!errors.amount}
                        onAmount={(n) => {
                            setAmount(n)
                            setErrors((e) => ({ ...e, amount: undefined }))
                        }}
                        onUnit={setUnit}
                    />
                </SentenceLine>
                <MessageBodyField
                    value={messageBody}
                    error={errors.message}
                    onChange={(v) => {
                        setMessageBody(v)
                        setErrors((e) => ({ ...e, message: undefined }))
                    }}
                />
            </div>
            <div className="mt-3 flex justify-end border-t pt-3">
                <Button
                    size="sm"
                    onClick={() => void submit()}
                    disabled={add.isPending}
                >
                    <Plus className="size-4" />
                    {m.care_cycle_add()}
                </Button>
            </div>
        </Card>
    )
}

// ---------------- Periodic cards ----------------

interface PeriodicDraft {
    amount: number
    unit: TimeUnit
    message_body: string
}

function PeriodicRow({ rule }: { rule: CarePeriodicRule }) {
    const update = useUpdatePeriodicRule()
    const del = useDeletePeriodicRule()
    const [draft, setDraft] = useState<PeriodicDraft>({
        amount: rule.amount,
        unit: rule.unit,
        message_body: rule.message_body,
    })

    const [errors, setErrors] = useState<{
        amount?: string
        message?: string
    }>({})

    const dirty =
        draft.amount !== rule.amount ||
        draft.unit !== rule.unit ||
        draft.message_body !== rule.message_body

    // save runs the mutation; the hook's onError shows the error toast, so on
    // failure we only skip the success toast (no second toast here).
    // Returns whether the write landed so callers can sync local state.
    async function save(body: {
        activity: string
        amount: number
        unit: TimeUnit
        enabled: boolean
        message_body: string
    }): Promise<boolean> {
        try {
            await update.mutateAsync({ id: rule.id, body })
            toast.success(m.care_cycle_rule_saved())
            return true
        } catch {
            // Error toast handled by the mutation's onError.
            return false
        }
    }

    // Save button: validate the draft, then persist it (keeping current enabled).
    async function persistDraft() {
        const errs = {
            amount: amountError(draft.amount),
            message: messageError(draft.message_body),
        }
        setErrors(errs)
        if (hasErrors(errs)) return
        const cleaned = { ...draft, message_body: draft.message_body.trim() }
        if (
            await save({
                activity: rule.activity,
                ...cleaned,
                enabled: rule.enabled,
            })
        ) {
            // Đồng bộ draft với giá trị đã lưu (body đã trim) — không thì so
            // sánh dirty lệch mãi và nút Lưu không chịu ẩn.
            setDraft(cleaned)
        }
    }

    // Toggle: persist only the enabled flag from the SAVED rule — never the
    // unsaved draft edits, and never blocked by draft validation.
    function toggleEnabled(enabled: boolean) {
        void save({
            activity: rule.activity,
            amount: rule.amount,
            unit: rule.unit,
            enabled,
            message_body: rule.message_body,
        })
    }

    return (
        <CardShell
            disabled={!rule.enabled}
            controls={
                <>
                    <Switch
                        checked={rule.enabled}
                        disabled={update.isPending}
                        onCheckedChange={toggleEnabled}
                        aria-label={m.care_cycle_col_enabled()}
                    />
                    <DeleteButton
                        disabled={del.isPending}
                        onClick={async () => {
                            // Xoá rule cũng dọn reminder định kỳ đã arm — hỏi
                            // lại trước, không có undo.
                            if (!window.confirm(m.care_cycle_delete_confirm()))
                                return
                            await del.mutateAsync(rule.id)
                            toast.success(m.care_cycle_rule_deleted())
                        }}
                    />
                </>
            }
            footer={
                dirty && (
                    <div className="mt-3 flex justify-end border-t pt-3">
                        <Button
                            size="sm"
                            onClick={() => void persistDraft()}
                            disabled={update.isPending}
                        >
                            {m.care_cycle_save()}
                        </Button>
                    </div>
                )
            }
        >
            <SentenceLine label={m.care_cycle_col_activity()}>
                {/* Activity is the rule's identity — fixed once created. */}
                <ActivitySelect value={rule.activity} disabled />
            </SentenceLine>
            <SentenceLine
                label={m.care_cycle_periodic_col_after()}
                error={errors.amount}
            >
                <AmountUnitInput
                    amount={draft.amount}
                    unit={draft.unit}
                    invalid={!!errors.amount}
                    onAmount={(n) => {
                        setDraft((d) => ({ ...d, amount: n }))
                        setErrors((e) => ({ ...e, amount: undefined }))
                    }}
                    onUnit={(u) => setDraft((d) => ({ ...d, unit: u }))}
                />
            </SentenceLine>
            <MessageBodyField
                value={draft.message_body}
                error={errors.message}
                onChange={(v) => {
                    setDraft((d) => ({ ...d, message_body: v }))
                    setErrors((e) => ({ ...e, message: undefined }))
                }}
            />
        </CardShell>
    )
}

function AddPeriodicRow({ existing }: { existing: string[] }) {
    const add = useAddPeriodicRule()
    const [activity, setActivity] = useState('')
    const [amount, setAmount] = useState(15)
    const [unit, setUnit] = useState<TimeUnit>('day')
    const [messageBody, setMessageBody] = useState('')
    const [errors, setErrors] = useState<{
        activity?: string
        amount?: string
        message?: string
    }>({})

    async function submit() {
        const errs = {
            activity: activity ? undefined : m.care_cycle_err_activity(),
            amount: amountError(amount),
            message: messageError(messageBody),
        }
        setErrors(errs)
        if (hasErrors(errs)) return
        try {
            await add.mutateAsync({
                body: {
                    activity,
                    amount,
                    unit,
                    enabled: true,
                    message_body: messageBody.trim(),
                },
            })
            toast.success(m.care_cycle_rule_saved())
            setActivity('')
            setAmount(15)
            setUnit('day')
            setMessageBody('')
            setErrors({})
        } catch {
            // Error toast handled by the mutation's onError.
        }
    }

    // Hide activities that already have a periodic rule (one per activity).
    const taken = new Set(existing)
    return (
        <Card className="gap-0 border-dashed p-4">
            <p className="mb-3 text-sm font-medium text-muted-foreground">
                {m.care_cycle_new_periodic_title()}
            </p>
            <div className="space-y-2.5">
                <SentenceLine
                    label={m.care_cycle_col_activity()}
                    error={errors.activity}
                >
                    <Select
                        value={activity}
                        onValueChange={(v) => {
                            setActivity(v)
                            setErrors((e) => ({ ...e, activity: undefined }))
                        }}
                    >
                        <SelectTrigger
                            aria-invalid={!!errors.activity}
                            className={cn(
                                'min-w-40',
                                errors.activity && 'border-destructive',
                            )}
                        >
                            <SelectValue
                                placeholder={m.care_cycle_from_placeholder()}
                            />
                        </SelectTrigger>
                        <SelectContent>
                            {ACTIVITIES.filter((a) => !taken.has(a.value)).map(
                                (a) => (
                                    <SelectItem key={a.value} value={a.value}>
                                        {a.label()}
                                    </SelectItem>
                                ),
                            )}
                        </SelectContent>
                    </Select>
                </SentenceLine>
                <SentenceLine
                    label={m.care_cycle_periodic_col_after()}
                    error={errors.amount}
                >
                    <AmountUnitInput
                        amount={amount}
                        unit={unit}
                        invalid={!!errors.amount}
                        onAmount={(n) => {
                            setAmount(n)
                            setErrors((e) => ({ ...e, amount: undefined }))
                        }}
                        onUnit={setUnit}
                    />
                </SentenceLine>
                <MessageBodyField
                    value={messageBody}
                    error={errors.message}
                    onChange={(v) => {
                        setMessageBody(v)
                        setErrors((e) => ({ ...e, message: undefined }))
                    }}
                />
            </div>
            <div className="mt-3 flex justify-end border-t pt-3">
                <Button
                    size="sm"
                    onClick={() => void submit()}
                    disabled={add.isPending}
                >
                    <Plus className="size-4" />
                    {m.care_cycle_periodic_add()}
                </Button>
            </div>
        </Card>
    )
}

// ---------------- Page ----------------

function CareRemindersPage() {
    const { data: rules = [], isLoading } = useCareCycleRules()
    const { data: periodicRules = [], isLoading: loadingPeriodic } =
        useCarePeriodicRules()

    return (
        <div className="space-y-4 page-pad md:space-y-6">
            <div className="flex min-w-0 items-start gap-2">
                <PageBackButton className="-my-2 md:-my-1" />
                <div className="space-y-1">
                    <h1 className="text-xl font-semibold md:text-2xl">
                        {m.care_cycle_page_title()}
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        {m.care_cycle_page_subtitle()}
                    </p>
                </div>
            </div>

            <div className="max-w-5xl space-y-4 md:space-y-6">
                <FormSection
                    icon={<Bell />}
                    title={m.care_cycle_workflow_title()}
                >
                    <p className="-mt-2 text-sm text-muted-foreground">
                        {m.care_cycle_workflow_sub()}
                    </p>
                    <div className="my-4 flex gap-2 rounded-lg border border-primary/20 bg-primary/5 p-3 text-sm text-primary">
                        <span aria-hidden>💡</span>
                        <span>{m.care_cycle_hint()}</span>
                    </div>

                    <div className="space-y-3">
                        {!isLoading && rules.length === 0 && (
                            <p className="text-sm text-muted-foreground">
                                {m.care_cycle_empty()}
                            </p>
                        )}
                        {/* Key theo id THUẦN: kèm updated_at thì mỗi lần gạt
                            switch (updated_at đổi) row remount và vứt sạch
                            draft đang gõ dở. Draft tự đồng bộ sau save thành
                            công trong persistDraft. */}
                        {rules.map((rule) => (
                            <WorkflowRow key={rule.id} rule={rule} />
                        ))}
                        <AddWorkflowRow />
                    </div>
                </FormSection>

                <FormSection
                    icon={<Bell />}
                    title={m.care_cycle_periodic_title()}
                >
                    <p className="-mt-2 text-sm text-muted-foreground">
                        {m.care_cycle_periodic_sub()}
                    </p>
                    <div className="my-4 flex gap-2 rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 text-sm text-amber-700 dark:text-amber-500">
                        <span aria-hidden>🔔</span>
                        <span>{m.care_cycle_periodic_hint()}</span>
                    </div>

                    <div className="space-y-3">
                        {!loadingPeriodic && periodicRules.length === 0 && (
                            <p className="text-sm text-muted-foreground">
                                {m.care_cycle_periodic_empty()}
                            </p>
                        )}
                        {/* Key theo id thuần — cùng lý do với WorkflowRow. */}
                        {periodicRules.map((rule) => (
                            <PeriodicRow key={rule.id} rule={rule} />
                        ))}
                        <AddPeriodicRow
                            existing={periodicRules.map((r) => r.activity)}
                        />
                    </div>
                </FormSection>
            </div>
        </div>
    )
}
